<template>
  <mass-delete-download-table
    :locations="locations"
    :folders="folders"
    :folders-loaded="foldersLoaded"
    :folders-total="foldersTotal"
    :files-to-load="filesToLoad"
    :file-values-map="fileValuesMap"
    @load-more="loadMoreItems"
    @view-all-files="viewAllFiles"
    @folder-expand="handleFolderExpand"
  />
</template>

<script lang="ts">
import {
  type AbnormalDataManipulationFile,
  type AbnormalDataManipulationFolder,
  useTicketStore,
} from "@/_store/tickets/ticket.module";
import { storeToRefs } from "pinia";
import { computed, defineComponent, onMounted, type PropType, reactive, ref } from "vue";
import { RouteName } from "@/constants/routes";
import type { TicketDetails } from "@/_store/tickets/tickets.module";
import { uniqueId } from "lodash";
import { useRouter } from "vue-router";
import MassDeleteDownloadTable from "../base-components/MassDeleteDownloadTable.vue";

export default defineComponent({
  components: {
    MassDeleteDownloadTable,
  },
  props: {
    ticket: {
      type: Object as PropType<TicketDetails>,
      required: true,
    },
  },
  setup(props) {
    const router = useRouter();
    const ticketStore = useTicketStore();
    const { folders, foldersLoaded, foldersTotal } = storeToRefs(ticketStore);
    const page = ref(0);
    const fileValuesMap = reactive<{ [path: string]: AbnormalDataManipulationFile[] }>({});

    const locations = computed(() => {
      return props.ticket.sections?.anomalyDetectionMetaData?.records
        ? props.ticket.sections?.anomalyDetectionMetaData?.records.map((item) => ({
            ...item,
            uniqueId: uniqueId(),
          }))
        : [];
    });

    const filesToLoad = computed(() => {
      return foldersTotal.value - (folders.value ?? []).length;
    });

    const getItems = async (virtualScroll = false) => {
      await ticketStore.getMassDeleteDownloadFolders({
        page: page.value,
        virtualScroll,
      });
    };

    const loadMoreItems = () => {
      page.value += 1;
      getItems(true);
    };

    const handleFolderExpand = async (folderId: string) => {
      const item = folders.value.find((v) => v.id === folderId);
      if (item) {
        const files = await ticketStore.getMassDeleteDownloadFiles({
          eventId: props.ticket.eventId,
          page: 0,
          folderId: item.id,
        });
        fileValuesMap[item.path] = files.items;
      }
    };

    const viewAllFiles = ({ id, path }: AbnormalDataManipulationFolder) => {
      router.push({
        name: RouteName.ABNORMAL_DATA_MANIPULATION_DETAILS_PAGE,
        query: {
          ticketId: props.ticket.eventId,
          path,
          pathId: id,
          eventTrigger: props.ticket.eventTrigger,
        },
      });
    };

    onMounted(async () => {
      await getItems();
    });

    return {
      page,
      folders,
      foldersLoaded,
      foldersTotal,
      locations,
      filesToLoad,
      loadMoreItems,
      handleFolderExpand,
      viewAllFiles,
      fileValuesMap,
    };
  },
});
</script>
